import { RowSelectedEvent } from "@ag-grid-community/core";
import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/alert";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { EventsRollbackConfirmDialog } from "features/admin/events/EventsRollbackConfirmDialog";
import { EventsTable } from "features/admin/events/EventsTable";
import { useCustomerEventAdminTableDataRetrieveSuspense } from "gen/endpoints/customer-event-admin/customer-event-admin";
import { useFundFamilyRetrieveSuspense } from "gen/endpoints/fund-family/fund-family";
import { CustomerEventRowMetadata } from "gen/model/customerEventRowMetadata";
import { isEmpty } from "lodash-es";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContainer from "shared/components/layout/PageContainer";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RowSelectionState } from "shared/components/table/data/DataTableTypes";
import { WarningIcon } from "shared/icons/WarningIcon";
import { rootRoute } from "shared/navigation/Routes";
import invariant from "shared/utils/TinyInvariant";
import { StringParam, useQueryParam } from "use-query-params";

export const EventsRollbackPage: React.FC = () => {
  const { t } = useTranslation(["common", "events"]);
  const navigate = useNavigate();

  const [fundFamilyId] = useQueryParam("fundFamilyId", StringParam);
  invariant(fundFamilyId, "fundFamilyId is required");

  const { data: fundFamily } = useFundFamilyRetrieveSuspense(fundFamilyId);
  const { data: eventTableData } =
    useCustomerEventAdminTableDataRetrieveSuspense({
      fund_family_id: fundFamilyId,
      omit_transaction_code_events: true,
    });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const onCancel = () => {
    navigate(rootRoute({}).admin({}).events({}).$);
  };

  const confirmRollback = useConfirmationDialog(EventsRollbackConfirmDialog, {
    fundFamilyId,
  });

  const onSubmit = () => {
    const selectedRowIds = new Set(
      Object.entries(rowSelection)
        .filter(([, value]) => value)
        .map(([key]) => key),
    );
    const events = eventTableData.rows
      .filter((row) => selectedRowIds.has(row.id))
      .map((row) => row.metadata as CustomerEventRowMetadata);

    // this is wrapped in a non-awaited function so the form does not
    // show the loading state while the dialog is open
    void (async () => {
      const result = await confirmRollback({ events });
      if (result) {
        navigate(rootRoute({}).admin({}).events({}).$);
      }
    })();
  };

  const onRowSelected = (event: RowSelectedEvent) => {
    const eventNodeIndex = event.node.rowIndex!;
    const eventNodeIsSelected = event.node.isSelected();
    event.api.forEachNode((node) => {
      const rowIndex = node.rowIndex!;
      if (eventNodeIsSelected && rowIndex < eventNodeIndex) {
        node.setSelected(true);
      } else if (!eventNodeIsSelected && rowIndex > eventNodeIndex) {
        node.setSelected(false);
      }
    });
  };

  const form = useForm();

  return (
    <PageContainer>
      <PageContent isFullHeight={true}>
        <PageBody>
          <PageHeader
            title={t("events:rollback.title")}
            helperText={t("events:rollback.instructions")}
          />
          <Alert
            status="warning"
            mb={4}
            data-ls-enabled={true}
            data-public={true}
          >
            <AlertIcon>
              <WarningIcon />
            </AlertIcon>
            <AlertDescription>{t("events:rollback.warning")}</AlertDescription>
          </Alert>
          <Form form={form} onSubmit={onSubmit}>
            <TableBox>
              <TableBoxHeader>
                <TableBoxTitle>
                  {t("events:rollback.table.title", {
                    name: fundFamily.name,
                  })}
                </TableBoxTitle>
                <DataTableBoxActions />
              </TableBoxHeader>
              <TableBoxBody>
                <QueryBoundary>
                  <EventsTable
                    fundFamilyId={fundFamilyId}
                    rollbackMode={true}
                    onRowSelected={onRowSelected}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    rowSelectionType="multiple"
                    rowSelectionCheckboxes={true}
                    enableRangeSelection={false}
                    suppressCellFocus={true}
                    sx={tableSx}
                  />
                </QueryBoundary>
              </TableBoxBody>
            </TableBox>
            <FormFooter>
              <FormFooterActions>
                <FormCancelButton onClick={onCancel} />
                <FormSubmitButton isDisabled={isEmpty(rowSelection)}>
                  {t("events:rollback.submit")}
                </FormSubmitButton>
              </FormFooterActions>
            </FormFooter>
          </Form>
        </PageBody>
      </PageContent>
    </PageContainer>
  );
};

const tableSx: SystemStyleObject = {
  ".ag-row.ag-row-selected": {
    "--ag-background-color": "var(--chakra-colors-error-50)",
    "--ag-odd-row-background-color": "var(--chakra-colors-error-50)",
    ".ag-checkbox-input-wrapper": {
      borderColor: "error.700",
      backgroundColor: "error.25",
      _after: {
        bgColor: "error.700",
      },
    },
  },
  ".ag-checkbox .ag-checkbox-input-wrapper": {
    _hover: {
      borderColor: "error.700",
      bgColor: "error.25",
    },
  },
};
